import React from "react";
import { Check, AlertTriangle, ShieldAlert, Ban, FileSpreadsheet } from "lucide-react";
import { cn } from "@/lib/utils";
import { Tooltip, TooltipContent, TooltipTrigger } from "@/components/ui/tooltip";
import { VALIDATION, VALVE_STATUS, STATUS_LABEL, STATUS_TONE } from "@/lib/sk";

const ICON = { validated: Check, assumed: AlertTriangle, needs_engineer: ShieldAlert, blocked: Ban };

export function ValidationBadge({ state, className, short = false, testId }) {
  const v = VALIDATION[state] || VALIDATION.validated;
  const Icon = ICON[state] || Check;
  return (
    <span className={cn("v-badge", v.cls, className)} data-testid={testId}>
      <Icon className="h-3 w-3" strokeWidth={2.5} />
      {short ? null : v.label}
    </span>
  );
}

export function ValveStatusBadge({ status, issues, className, testId }) {
  const v = VALVE_STATUS[status] || VALVE_STATUS.mapped;
  const badge = (
    <span className={cn("v-badge", v.cls, className)} data-testid={testId}>
      {status === "mapped" ? <Check className="h-3 w-3" strokeWidth={2.5} /> : <AlertTriangle className="h-3 w-3" strokeWidth={2.5} />}
      {v.label}
    </span>
  );
  if (!issues?.length) return badge;
  return (
    <Tooltip>
      <TooltipTrigger asChild>{badge}</TooltipTrigger>
      <TooltipContent side="left" className="max-w-[300px] rounded-xl border-black/[0.08] bg-white p-3 text-foreground shadow-float">
        <ul className="space-y-1.5 text-[12px] leading-snug">
          {issues.map((i, k) => (
            <li key={k} className="flex gap-2"><span className={cn("mt-[5px] h-1.5 w-1.5 shrink-0 rounded-full", i.severity === "blocked" ? "bg-[hsl(var(--v-blocked))]" : i.severity === "engineer" ? "bg-[hsl(var(--v-eng))]" : "bg-[hsl(var(--v-assumed))]")} />{i.message}</li>
          ))}
        </ul>
      </TooltipContent>
    </Tooltip>
  );
}

export function SourceRef({ source, field, className }) {
  if (!source) return null;
  const cell = field ? source.cells?.[field] : null;
  const label = cell ? `${cell}` : `rad ${source.row}`;
  return (
    <Tooltip>
      <TooltipTrigger asChild>
        <span className={cn("src-ref cursor-default", className)}>
          <FileSpreadsheet className="h-2.5 w-2.5" />
          {label}
        </span>
      </TooltipTrigger>
      <TooltipContent side="top" className="rounded-lg border-black/[0.08] bg-white px-2.5 py-1.5 font-mono text-[11px] text-foreground shadow-float">
        Kilde: {source.file} · {source.sheet} · {cell || `rad ${source.row}`}
      </TooltipContent>
    </Tooltip>
  );
}

export function AiTag({ children = "Foreslått", className }) {
  return (
    <span className={cn("v-badge v-assumed", className)}>
      {children}
    </span>
  );
}

export function ProjectStatusChip({ status, className, testId }) {
  return <span className={cn("inline-flex h-[22px] items-center rounded-md px-2 text-[11px] font-semibold", STATUS_TONE[status] || STATUS_TONE.draft, className)} data-testid={testId}>{STATUS_LABEL[status] || status}</span>;
}

export function Stamp({ label, stamp, pending, className, testId }) {
  return (
    <span className={cn("inline-flex items-center gap-1.5 rounded-md px-2 py-1 text-[11.5px]", stamp ? "bg-[hsl(var(--v-ok-bg))] text-[hsl(var(--v-ok))]" : "bg-black/[0.04] text-muted-foreground", className)} data-testid={testId}>
      {stamp ? <Check className="h-3 w-3" strokeWidth={2.5} /> : <span className="h-1.5 w-1.5 rounded-full bg-current opacity-50" />}
      {stamp ? `${label} · ${stamp.by}` : pending || `${label} mangler`}
    </span>
  );
}
